import React from 'react';

import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, {
	ErrorMessage,
	Field,
	FormHeader,
	FormSection,
	HelperMessage,
	RequiredAsterisk,
} from '@atlaskit/form';

import Textfield from '@atlaskit/textfield';
import { RadioGroup } from '@atlaskit/radio';

import type { OptionsPropType } from '@atlaskit/radio/types';
import { cssMap } from '@atlaskit/css';
import { useIntl } from '@atlassian/jira-intl';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fetchApplinkManifestForUrl, fetchThisManifest } from '../../../../rest/index.tsx';
import type { CreateFlowVariant, StageProps } from '../../../../common/types.tsx';
import { APPLINKS_IS_OAUTH2_SUPPORTED_PROVIDER_CHECK } from '../../../../common/constants.tsx';
import commonMessages from '../../../messages.tsx';
import { SubpageTemplate } from '../template/SubpageTemplate.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	fullContainer: {
		height: '100%',
	},
});

const linkTypeRadioOptions: OptionsPropType = [
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ name: 'linkType', value: 'OAUTH2.0', label: 'Atlassian product via OAuth 2.0' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ name: 'linkType', value: 'OAUTH1.0', label: 'Direct application link via OAuth 1.0' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ name: 'linkType', value: 'TUNNEL', label: 'Existing application tunnel' },
];

type CreateSubpageProps = {
	onError: (error: string) => void;
	onSuccess: (newState: StageProps) => void;
	onCancel: () => void;
};

const CreateSubpage = (props: CreateSubpageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const onFormSubmit = async (data: { linkType: CreateFlowVariant; remoteUrl: string }) => {
		try {
			const remoteManifestPromise = fetchApplinkManifestForUrl(data.remoteUrl);
			const localManifestPromise = fetchThisManifest();
			const remoteManifest = await remoteManifestPromise;
			const localManifest = await localManifestPromise;

			// Check the remote application supports the selected variant type
			if (
				data.linkType === 'OAUTH2.0' &&
				!remoteManifest.inboundAuthenticationTypes.includes(
					APPLINKS_IS_OAUTH2_SUPPORTED_PROVIDER_CHECK,
				)
			) {
				// TODO: Better error message - maybe a custom dialogue?
				throw new Error(
					'The remote application does not support OAuth 2.0 authentication. Please select a different link type.',
				);
			}

			props.onSuccess({
				kind: 'review',
				variant: data.linkType,
				localManifest,
				remoteManifest,
				rpcUrl: data.remoteUrl,
			});
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			if (data.linkType === 'OAUTH1.0') {
				props.onSuccess({
					kind: 'manual-create',
					variant: 'OAUTH1.0',
					error: maybeError ? maybeError.message : formatMessage(commonMessages.unknownErrorText),
				});
			} else {
				log.safeErrorWithoutCustomerData(
					'applinks.administration.list.creation-flow-dialog.create.submitCreateForm',
					'Failed to submit create form',
					maybeError,
				);
				props.onError(
					maybeError ? maybeError.message : formatMessage(commonMessages.unknownErrorText),
				);
			}
		}
	};

	return (
		<Form<{ linkType: CreateFlowVariant; remoteUrl: string }>
			onSubmit={onFormSubmit}
			xcss={styles.fullContainer}
		>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<SubpageTemplate
						variant={null}
						kind="create"
						buttons={
							<ButtonGroup label="Form submit options">
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<Button appearance="subtle" onClick={props.onCancel}>
									Cancel
								</Button>
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<Button type="submit" appearance="primary" isLoading={submitting}>
									Next
								</Button>
							</ButtonGroup>
						}
						content={
							<Stack>
								<FormHeader title={formatMessage(messages.createHeaderText)}>
									<Stack space="space.200">
										<Box>
											{`${formatMessage(messages.applicationLinkInformationText)} `}
											<Link
												href="https://support.atlassian.com/jira-cloud-administration/docs/use-applinks-to-link-to-atlassian-products/"
												target="_blank"
											>
												{' '}
												{formatMessage(messages.applicationLinkHowToLinkText)}
											</Link>
										</Box>
										{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
										<Text size="small">
											Required fields are marked with an asterisk <RequiredAsterisk />
										</Text>
									</Stack>
								</FormHeader>
								<FormSection title="Application link type">
									<Field label="Link type" name="linkType" defaultValue="OAUTH2.0" isRequired>
										{({ fieldProps }) => (
											<>
												<RadioGroup {...fieldProps} options={linkTypeRadioOptions} />
												{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
												<HelperMessage>
													Check the application&apos;s documentation for supported link types
												</HelperMessage>
											</>
										)}
									</Field>
								</FormSection>
								<FormSection title="External application details">
									<Field name="remoteUrl" label="Application URL" isRequired>
										{({ fieldProps, error }) => (
											<>
												<Textfield
													autoComplete="off"
													placeholder="https://example.atlassian.net"
													{...fieldProps}
												/>
												{!error && (
													// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
													<HelperMessage>Enter the URL for the Atlassian product </HelperMessage>
												)}
												{/* TODO: Add validation */}
												{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
												{error && <ErrorMessage>You must enter a valid URL</ErrorMessage>}
											</>
										)}
									</Field>
								</FormSection>
							</Stack>
						}
					/>
				</form>
			)}
		</Form>
	);
};

export default CreateSubpage;
